"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import { trapTabFocus } from "@/components/utils/modal/focusTrap";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import { type MouseEvent as ReactMouseEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ParticipationDropVoteDetailsContent } from "./ParticipationDropVoteDetailsContent";
type VoteDetailsTab = "voters" | "logs";
type VoteDetailsTriggerDensity = "default" | "compact";
interface ParticipationDropVoteDetailsTriggerProps {
  readonly drop: ApiDrop;
  readonly density?: VoteDetailsTriggerDensity | undefined;
}
const VIEWPORT_PADDING_PX = 16;
const POPOVER_GAP_PX = 8;
const POPOVER_WIDTH_PX = 360;
const isInsideElement = (element: HTMLElement | null, target: EventTarget | null): boolean => {
  if (!element || typeof globalThis.Node === "undefined") {
    return false;
  }
  return target instanceof globalThis.Node && element.contains(target);
};
export default function ParticipationDropVoteDetailsTrigger(t0) {
  const $ = _c(64);
  const {
    drop,
    density: t1
  } = t0;
  const density = t1 === undefined ? "default" : t1;
  const isMobileScreen = useIsMobileScreen();
  const isTouchDevice = useIsTouchDevice();
  const useSheet = isMobileScreen || isTouchDevice;
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("voters");
  const [popoverPosition, setPopoverPosition] = useState(null);
  const triggerRef = useRef(null);
  const surfaceRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsOpen(false);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const closeDetails = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setActiveTab("voters");
      setPopoverPosition(null);
      setIsOpen(true);
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const openDetails = t3;
  let t4;
  if ($[2] !== isOpen) {
    t4 = event => {
      event.stopPropagation();
      if (isOpen) {
        closeDetails();
        return;
      }
      openDetails();
    };
    $[2] = isOpen;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const toggleDetails = t4;
  let t5;
  if ($[4] !== isOpen || $[5] !== useSheet) {
    t5 = () => {
      if (!isOpen || useSheet) {
        return;
      }
      const button = triggerRef.current;
      const surface = surfaceRef.current;
      if (!button || !surface) {
        return;
      }
      const buttonRect = button.getBoundingClientRect();
      const surfaceWidth = surface.offsetWidth || POPOVER_WIDTH_PX;
      const surfaceHeight = surface.offsetHeight;
      const viewportWidth = globalThis.innerWidth || globalThis.document.documentElement.clientWidth || POPOVER_WIDTH_PX + VIEWPORT_PADDING_PX * 2;
      const viewportHeight = globalThis.innerHeight || globalThis.document.documentElement.clientHeight || surfaceHeight + VIEWPORT_PADDING_PX * 2;
      const maxLeft = Math.max(VIEWPORT_PADDING_PX, viewportWidth - VIEWPORT_PADDING_PX - surfaceWidth);
      const left = Math.min(Math.max(buttonRect.left, VIEWPORT_PADDING_PX), maxLeft);
      const availableBelow = viewportHeight - buttonRect.bottom - VIEWPORT_PADDING_PX;
      const availableAbove = buttonRect.top - VIEWPORT_PADDING_PX;
      const shouldOpenAbove = surfaceHeight > 0 && availableBelow < surfaceHeight + POPOVER_GAP_PX && availableAbove > availableBelow;
      const unclampedTop = shouldOpenAbove ? buttonRect.top - surfaceHeight - POPOVER_GAP_PX : buttonRect.bottom + POPOVER_GAP_PX;
      const maxTop = Math.max(VIEWPORT_PADDING_PX, viewportHeight - VIEWPORT_PADDING_PX - surfaceHeight);
      const top = Math.min(Math.max(unclampedTop, VIEWPORT_PADDING_PX), maxTop);
      setPopoverPosition({
        left,
        top
      });
    };
    $[4] = isOpen;
    $[5] = useSheet;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const updatePopoverPosition = t5;
  let t6;
  let t7;
  if ($[7] !== isOpen || $[8] !== useSheet) {
    t6 = () => {
      if (!isOpen || useSheet) {
        return;
      }
      const handlePointerDown = event_0 => {
        if (isInsideElement(surfaceRef.current, event_0.target) || isInsideElement(triggerRef.current, event_0.target)) {
          return;
        }
        closeDetails();
      };
      const handleKeyDown = event_1 => {
        if (event_1.key === "Escape") {
          event_1.preventDefault();
          closeDetails();
          return;
        }
        const surface_0 = surfaceRef.current;
        if (surface_0) {
          trapTabFocus(event_1, surface_0);
        }
      };
      globalThis.document.addEventListener("mousedown", handlePointerDown);
      globalThis.document.addEventListener("touchstart", handlePointerDown);
      globalThis.document.addEventListener("keydown", handleKeyDown);
      return () => {
        globalThis.document.removeEventListener("mousedown", handlePointerDown);
        globalThis.document.removeEventListener("touchstart", handlePointerDown);
        globalThis.document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t7 = [closeDetails, isOpen, useSheet];
    $[7] = isOpen;
    $[8] = useSheet;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] !== isOpen || $[12] !== useSheet) {
    t8 = () => {
      if (!isOpen || useSheet) {
        return;
      }
      const activeElement = globalThis.document.activeElement;
      previouslyFocusedElementRef.current = typeof globalThis.HTMLElement !== "undefined" && activeElement instanceof globalThis.HTMLElement ? activeElement : triggerRef.current;
      surfaceRef.current?.focus({
        preventScroll: true
      });
      return () => {
        const restoreTarget = previouslyFocusedElementRef.current?.isConnected ? previouslyFocusedElementRef.current : triggerRef.current;
        restoreTarget?.focus({
          preventScroll: true
        });
        previouslyFocusedElementRef.current = null;
      };
    };
    t9 = [isOpen, useSheet];
    $[11] = isOpen;
    $[12] = useSheet;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[15] !== updatePopoverPosition) {
    t10 = () => {
      updatePopoverPosition();
    };
    t11 = [updatePopoverPosition];
    $[15] = updatePopoverPosition;
    $[16] = t10;
    $[17] = t11;
  } else {
    t10 = $[16];
    t11 = $[17];
  }
  useLayoutEffect(t10, t11);
  let t12;
  let t13;
  if ($[18] !== isOpen || $[19] !== updatePopoverPosition || $[20] !== useSheet) {
    t12 = () => {
      if (!isOpen || useSheet) {
        return;
      }
      const surface_1 = surfaceRef.current;
      if (!surface_1) {
        return;
      }
      if (typeof globalThis.ResizeObserver === "undefined") {
        updatePopoverPosition();
        return;
      }
      const resizeObserver = new globalThis.ResizeObserver(() => {
        updatePopoverPosition();
      });
      resizeObserver.observe(surface_1);
      return () => resizeObserver.disconnect();
    };
    t13 = [isOpen, updatePopoverPosition, useSheet];
    $[18] = isOpen;
    $[19] = updatePopoverPosition;
    $[20] = useSheet;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[23] !== isOpen || $[24] !== updatePopoverPosition || $[25] !== useSheet) {
    t14 = () => {
      if (!isOpen || useSheet) {
        return;
      }
      globalThis.addEventListener("resize", updatePopoverPosition);
      globalThis.addEventListener("scroll", updatePopoverPosition, true);
      return () => {
        globalThis.removeEventListener("resize", updatePopoverPosition);
        globalThis.removeEventListener("scroll", updatePopoverPosition, true);
      };
    };
    t15 = [isOpen, updatePopoverPosition, useSheet];
    $[23] = isOpen;
    $[24] = updatePopoverPosition;
    $[25] = useSheet;
    $[26] = t14;
    $[27] = t15;
  } else {
    t14 = $[26];
    t15 = $[27];
  }
  useEffect(t14, t15);
  const t16 = !useSheet;
  let t17;
  if ($[28] !== activeTab || $[29] !== drop || $[30] !== isOpen || $[31] !== t16) {
    t17 = <ParticipationDropVoteDetailsContent drop={drop} activeTab={activeTab} isOpen={isOpen} onActiveTabChange={setActiveTab} onClose={closeDetails} showHeader={t16} />;
    $[28] = activeTab;
    $[29] = drop;
    $[30] = isOpen;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const detailsContent = t17;
  let t18;
  if ($[33] !== detailsContent || $[34] !== isOpen || $[35] !== useSheet) {
    t18 = isOpen && useSheet && typeof globalThis.document !== "undefined" ? createPortal(<MobileWrapperDialog title="Votes" isOpen={isOpen} onClose={closeDetails} fixedHeight={true} tall={true} showScrollbar={true} headerClassName="tw-py-4"><div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">{detailsContent}</div></MobileWrapperDialog>, globalThis.document.body) : null;
    $[33] = detailsContent;
    $[34] = isOpen;
    $[35] = useSheet;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const mobileSurface = t18;
  let t19;
  if ($[37] !== detailsContent || $[38] !== isOpen || $[39] !== popoverPosition || $[40] !== useSheet) {
    t19 = isOpen && !useSheet && typeof globalThis.document !== "undefined" ? createPortal(<div className="tailwind-scope tw-fixed tw-z-[1200]" style={{
      left: popoverPosition?.left ?? 0,
      top: popoverPosition?.top ?? 0,
      visibility: popoverPosition ? "visible" : "hidden",
      width: POPOVER_WIDTH_PX
    }} onClick={_temp}><div ref={surfaceRef} role="dialog" aria-label="Votes" tabIndex={-1} className="tw-flex tw-max-h-[26rem] tw-w-[22.5rem] tw-max-w-[calc(100vw-2rem)] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-shadow-2xl tw-shadow-black/40">{detailsContent}</div></div>, globalThis.document.body) : null;
    $[37] = detailsContent;
    $[38] = isOpen;
    $[39] = popoverPosition;
    $[40] = useSheet;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const desktopSurface = t19;
  const isCompact = density === "compact";
  const triggerClassName = `tw-inline-flex tw-cursor-pointer tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-shadow-sm tw-transition-colors tw-duration-200 tw-ease-out focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400/60 desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-iron-100 ${isCompact ? "tw-gap-1 tw-px-1.5 tw-py-0.5 tw-text-xs tw-leading-4" : "tw-gap-1.5 tw-px-2 tw-py-1 tw-text-sm tw-leading-5"}`;
  const chevronClassName = `tw-flex-shrink-0 tw-text-iron-500 tw-transition-transform tw-duration-200 ${isCompact ? "tw-size-3" : "tw-size-3.5"} ${isOpen ? "tw-rotate-180" : ""}`;
  let t20;
  if ($[42] !== drop.raters_count) {
    t20 = formatNumberWithCommas(drop.raters_count);
    $[42] = drop.raters_count;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const t21 = `View voters and vote log for ${t20} ${drop.raters_count === 1 ? "voter" : "voters"}`;
  let t22;
  if ($[44] !== drop.raters_count) {
    t22 = formatNumberWithCommas(drop.raters_count);
    $[44] = drop.raters_count;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t22) {
    t23 = <span className="tw-font-medium tw-text-iron-50">{t22}</span>;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  const t24 = drop.raters_count === 1 ? "voter" : "voters";
  let t25;
  if ($[48] !== t24) {
    t25 = <span className="tw-font-normal tw-text-iron-400">{t24}</span>;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== chevronClassName) {
    t26 = <ChevronDownIcon aria-hidden="true" className={chevronClassName} />;
    $[50] = chevronClassName;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== isOpen || $[53] !== t21 || $[54] !== t23 || $[55] !== t25 || $[56] !== t26 || $[57] !== toggleDetails || $[58] !== triggerClassName) {
    t27 = <button ref={triggerRef} type="button" aria-haspopup="dialog" aria-expanded={isOpen} aria-label={t21} onClick={toggleDetails} className={triggerClassName}>{t23}{t25}{t26}</button>;
    $[52] = isOpen;
    $[53] = t21;
    $[54] = t23;
    $[55] = t25;
    $[56] = t26;
    $[57] = toggleDetails;
    $[58] = triggerClassName;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== desktopSurface || $[61] !== mobileSurface || $[62] !== t27) {
    t28 = <>{t27}{mobileSurface}{desktopSurface}</>;
    $[60] = desktopSurface;
    $[61] = mobileSurface;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  return t28;
}
function _temp(event_2) {
  return event_2.stopPropagation();
}